'use client';
import { formatCents } from 'components/Shop/helpers';
import { useToastStore } from 'components/ToastManager/useToasts';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { useSWRConfig } from 'swr';
import { useApi } from '~/hooks/useApi';
import { useDebouncedValue } from '~/hooks/useDebouncedValue';
import useAuth from '~/hooks/useAuth';
import { BillingCatalog, BillingOverview, BillingQuoteResult, BillingTier } from './types';

export const BILLING_OVERVIEW_KEY = 'billing/subscription';
export const BILLING_CATALOG_KEY = 'billing/catalog';
export const BILLING_CREDIT_KEY = 'billing/credit';
export const BILLING_COUPONS_KEY = 'billing/coupons';
export const BILLING_PAYMENT_METHODS_KEY = 'billing/payment-methods';

export const formatMoney = (cents: number | null | undefined, currency?: string | null) => {
    try {
        return formatCents(cents ?? 0, 'de-DE', currency || 'EUR');
    } catch {
        return formatCents(cents ?? 0, 'de-DE', 'EUR');
    }
};

export const formatBps = (bps: number | null | undefined) =>
    `${((bps ?? 0) / 100).toLocaleString('de-DE', { maximumFractionDigits: 2 })} %`;

export type TierBound = 'min_members' | 'min_participants';

// die Obergrenze ist die nächste höhere Untergrenze, damit Nova nur eine Zahl je Stufe braucht
export const tierRanges = (tiers: BillingTier[], bound: TierBound) =>
    tiers.map((tier, index) => {
        const from = tier[bound];
        if (from === null) return null;
        const next = tiers.slice(index + 1).find((item) => item[bound] !== null && item[bound] > from);

        return { from, to: next ? (next[bound] as number) - 1 : null };
    });

export const formatBillingDate = (value?: string | null) => (value ? moment(value).format('L') : '');

// Laravel JsonResources arrive wrapped in { data }, the plain JSON endpoints do not.
export const unwrapResource = <T>(response: any): T =>
    response && typeof response === 'object' && 'data' in response && Object.keys(response).length === 1
        ? response.data
        : response;

export const useBillingOverview = (active = true) =>
    useSWR<BillingOverview>(active ? BILLING_OVERVIEW_KEY : null, { revalidateOnFocus: false });

export const useBillingCatalog = (active = true) =>
    useSWR<BillingCatalog>(active ? BILLING_CATALOG_KEY : null, { revalidateOnFocus: false });

/**
 * Self service is offered to people who can act for the club. user.clubs carries no full_access flag, so this
 * is the same membership check the subscription page uses.
 */
export const useSelfServiceBooking = (clubSlug?: string) => {
    const { relativeInClub } = useAuth();
    const isRelative = relativeInClub(clubSlug);
    const { data } = useBillingCatalog(isRelative);

    return isRelative && data?.enabled === true;
};

export const bookModuleHref = (planKey: string) => `/home/subscription?module=${encodeURIComponent(planKey)}`;

export const toast = (type: 'success' | 'error' | 'info', description: string) =>
    useToastStore.getState().addToast({ type, description });

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

/**
 * Runs a booking action, writes the returned overview into the cache and toasts the server message. Resolves
 * with the response body, or null when the request failed.
 */
export const useBillingRequest = () => {
    const api = useApi();
    const { mutate } = useSWRConfig();
    const { t } = useTranslation('mollie');

    return useCallback(
        <T = any>(url: string, method: Method, data?: any, options: { silent?: boolean } = {}) =>
            new Promise<T | null>((resolve) => {
                void api({
                    url,
                    method,
                    data,
                    muted: true,
                    onSuccess: (response) => {
                        if (response?.subscription) {
                            void mutate(BILLING_OVERVIEW_KEY, response.subscription, { revalidate: false });
                        } else {
                            void mutate(BILLING_OVERVIEW_KEY);
                        }
                        void mutate(BILLING_CATALOG_KEY);
                        if (!options.silent && response?.message) toast('success', response.message);
                        resolve(response || ({} as T));
                    },
                    onFailure: (response) => {
                        toast('error', response?.data?.message || t('billing.errors.generic'));
                        resolve(null);
                    }
                });
            }),
        [api, mutate, t]
    );
};

export interface QuoteInput {
    size_tier?: string | null;
    modules: string[];
    coupon_code?: string | null;
}

/**
 * Live price for a selection. Debounced, and an answer that arrives after a newer request was sent is dropped.
 */
export const useBillingQuote = (input: QuoteInput | null, delay = 400) => {
    const api = useApi();
    const debounced = useDebouncedValue(input, delay, true);
    const [quote, setQuote] = useState<BillingQuoteResult | null>(null);
    const [error, setError] = useState<{ message: string; errors?: Record<string, string[]> } | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const requestId = useRef(0);
    const key = JSON.stringify(debounced);

    useEffect(() => {
        if (!debounced) return;

        const id = ++requestId.current;
        setIsLoading(true);
        void api({
            url: 'billing/quote',
            method: 'POST',
            data: { ...debounced, coupon_code: debounced.coupon_code || undefined },
            muted: true,
            onSuccess: (response) => {
                if (id !== requestId.current) return;
                setQuote(unwrapResource<BillingQuoteResult>(response));
                setError(null);
                setIsLoading(false);
            },
            onFailure: (response) => {
                if (id !== requestId.current) return;
                setQuote(null);
                setError({ message: response?.data?.message ?? '', errors: response?.data?.errors });
                setIsLoading(false);
            }
        });
    }, [key]);

    if (!input) return { quote: null, error: null, isLoading: false };

    return { quote, error, isLoading: isLoading || JSON.stringify(input) !== key };
};
